import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  Account,
  AccountPaymentCredential,
  AccountType,
  ChargemasterFeeScheduleCoverage,
  ClaimRemitStatus,
  Customer,
  ExistingAccount,
  ExpectedClaimTypes,
  ExpectedRateTypes,
  GetZelisAccount,
  PhiChallengeFactor,
  PhiConsentExpiration,
  SsoSettings,
  Twilio,
  ZelisRouterAccount,
} from '@rivethealth/data/account';
import { PhiRequestSetting } from '@rivethealth/data/consent';
import { TopPayersByFeeSchedules } from '@rivethealth/data/payer';
import { map, Observable } from 'rxjs';
import { AdminCellApiTranslator } from './cell';
import { API_URL, ROUTER_API_URL } from './common';

@Injectable({ providedIn: 'root' })
export class AccountService {
  constructor(
    @Inject(API_URL) private readonly apiUrl: string,
    @Inject(ROUTER_API_URL) private readonly routerApiUrl: string,
    private readonly http: HttpClient,
    private readonly adminCellApiTranslator: AdminCellApiTranslator,
  ) {}

  getAccountsFromCurrentCell(accountType?: string) {
    return this.http.get<ExistingAccount[]>(`${this.apiUrl}/accounts`, {
      params: {
        ...(accountType ? { accountType: accountType } : {}),
      },
    });
  }

  getAccountsFromAllCells(
    accountType?: string,
  ): Observable<
    (ExistingAccount & { cell: string; accountType: AccountType })[]
  > {
    return this.adminCellApiTranslator
      .getAll<
        (ExistingAccount & {
          accountType: AccountType;
        })[]
      >(`${this.apiUrl}/accounts`, {
        ...(accountType && { accountType }),
      })
      .pipe(
        map(results =>
          results.flatMap(result =>
            result.response.map(r => ({ ...r, cell: result.cell })),
          ),
        ),
      );
  }

  getAccount(accountId: number) {
    return this.http.get<ExistingAccount>(
      `${this.apiUrl}/accounts/${accountId}`,
    );
  }

  getZelisAccount(accountId: number) {
    return this.http.get<GetZelisAccount>(
      `${this.routerApiUrl}/accounts/${accountId}/zelis`,
    );
  }

  getAccountIdByZelisProviderRef(providerRef: string) {
    return this.http.get<ZelisRouterAccount>(
      `${this.routerApiUrl}/accounts?providerId=${providerRef}`,
    );
  }

  getExpectedRateTypes(accountId: number) {
    return this.http.get<ExpectedRateTypes>(
      `${this.apiUrl}/accounts/${accountId}/rate-types`,
    );
  }

  getExpectedClaimTypes(accountId: number) {
    return this.http.get<ExpectedClaimTypes>(
      `${this.apiUrl}/accounts/${accountId}/claim-types`,
    );
  }

  changeExpectedRateType(
    accountId: number,
    rateType: string,
    newValue: boolean,
  ) {
    return this.http.post<void>(
      `${this.apiUrl}/accounts/${accountId}/rate-types`,
      { rateType, newValue },
    );
  }

  changeExpectedClaimType(
    accountId: number,
    claimType: 'claim' | 'remit',
    newValue: boolean,
  ) {
    return this.http.post<void>(
      `${this.apiUrl}/accounts/${accountId}/claim-types`,
      { claimType, newValue },
    );
  }

  getAccountPaymentCredential(accountId: number) {
    return this.http.get<AccountPaymentCredential>(
      `${this.apiUrl}/accounts/${accountId}/payment-credential`,
    );
  }

  setAccountPaymentCredential(
    accountId: number,
    credential: AccountPaymentCredential,
  ) {
    return this.http.put<void>(
      `${this.apiUrl}/accounts/${accountId}/payment-credential`,
      credential ?? {},
    );
  }

  createAccount(account: Account) {
    return this.http.post<ExistingAccount>(`${this.apiUrl}/accounts`, account);
  }

  modifyAccount(accountId: number, account: Account) {
    return this.http.put<void>(`${this.apiUrl}/accounts/${accountId}`, account);
  }

  expireAccount(accountId: number, account: ExistingAccount | null) {
    return this.http.put<void>(
      `${this.apiUrl}/accounts/${accountId}/expire`,
      account,
    );
  }

  getPhiConsentExpiration(accountId: number) {
    return this.http.get<PhiConsentExpiration>(
      `${this.apiUrl}/accounts/${accountId}/phi-consent-expiration`,
    );
  }

  setPhiConsentExpiration(accountId: number, days: number) {
    return this.http.put<void>(
      `${this.apiUrl}/accounts/${accountId}/phi-consent-expiration`,
      {
        days,
      },
    );
  }

  getPhiChallengeFactors(accountId: number) {
    return this.http.get<PhiChallengeFactor[]>(
      `${this.apiUrl}/accounts/${accountId}/phi-challenge-factor`,
    );
  }

  setPhiChallengeFactors(accountId: number, factors: PhiChallengeFactor[]) {
    return this.http.put<void>(
      `${this.apiUrl}/accounts/${accountId}/phi-challenge-factor`,
      factors,
    );
  }

  getPhiSmsSetting(accountId: number) {
    return this.http.get<PhiRequestSetting>(
      `${this.apiUrl}/accounts/${accountId}/phi-request-sms-setting`,
    );
  }

  setPhiSmsSetting(accountId: number, phiSmsSetting: PhiRequestSetting) {
    return this.http.put<void>(
      `${this.apiUrl}/accounts/${accountId}/phi-request-sms-setting`,
      phiSmsSetting,
    );
  }

  getPhiEmailSetting(accountId: number) {
    return this.http.get<PhiRequestSetting>(
      `${this.apiUrl}/accounts/${accountId}/phi-request-email-setting`,
    );
  }

  setPhiEmailSetting(accountId: number, phiEmailSetting: PhiRequestSetting) {
    return this.http.put<void>(
      `${this.apiUrl}/accounts/${accountId}/phi-request-email-setting`,
      phiEmailSetting,
    );
  }

  twilioRegisterBrand = (accountId: number, customer: Customer) => {
    return this.http.post<Twilio>(
      `${this.apiUrl}/accounts/${accountId}/twilio-register-brand`,
      customer,
    );
  };

  transferMessagingService = (accountId: number, customer: Customer) => {
    return this.http.post<Twilio>(
      `${this.apiUrl}/accounts/${accountId}/transfer-twilio-messaging`,
      customer,
    );
  };

  uploadIdpXml(accountId: number, xml: string) {
    return this.http.post<string>(
      `${this.apiUrl}/accounts/${accountId}/upload-idp-xml`,
      { xml },
    );
  }

  getSsoSettings(accountId: number) {
    return this.http.get<SsoSettings>(
      `${this.routerApiUrl}/accounts/${accountId}/sso-settings`,
    );
  }

  getIdentityProvider(accountId: number) {
    return this.http.get<{ file: string }>(
      `${this.apiUrl}/accounts/${accountId}/identity-provider-metadata`,
    );
  }

  deleteIdentityProviderXml(accountId: number) {
    return this.http.delete<void>(
      `${this.apiUrl}/accounts/${accountId}/delete-identity-provider`,
    );
  }

  getClaimRemitStatus(accountId: number) {
    return this.http.get<ClaimRemitStatus>(
      `${this.apiUrl}/accounts/dashboard/claim-remit-status/${accountId}`,
    );
  }

  getClaimRemitChart(accountId: number) {
    return this.http.get<{
      start: Date;
      end: Date;
      series: any[];
    }>(`${this.apiUrl}/accounts/dashboard/claim-remit-chart/${accountId}`);
  }

  topPayersByPatientInsurances(accountId: number) {
    return this.http.get<any>(
      `${this.apiUrl}/accounts/dashboard/top-payers-by-patient-insurances/${accountId}`,
    );
  }

  topPayersByFeeSchedules(accountId: number) {
    return this.http.get<TopPayersByFeeSchedules[]>(
      `${this.apiUrl}/accounts/dashboard/top-payers-by-fee-schedules/${accountId}`,
    );
  }

  topPayersByClaimsAndRemits(accountId: number) {
    return this.http.get<any>(
      `${this.apiUrl}/accounts/dashboard/top-payers-by-claims-and-remits/${accountId}`,
    );
  }

  getChargemasterRatesCoverage(accountId: number) {
    return this.http.get<ChargemasterFeeScheduleCoverage>(
      `${this.apiUrl}/accounts/dashboard/${accountId}/chargemaster-rates-coverage`,
    );
  }

  getFeeScheduleCodeMatchup(
    accountId: number,
    scheduleId: number,
    facilityType: string,
  ) {
    return this.http.get<{
      chargemasterCodes: string[];
      matchedCodes: string[];
    }>(
      `${this.apiUrl}/accounts/dashboard/${accountId}/fee-schedule-code-matchup/${scheduleId}`,
      {
        params: {
          facilityType: facilityType,
        },
      },
    );
  }

  getInsuranceRatesPercent(accountId: number) {
    return this.http.get<
      { facility: number; nonFacility: number; asc: number }[]
    >(`${this.apiUrl}/accounts/dashboard/insurance-rates-percent/${accountId}`);
  }

  feeScheduleUnmatchedProducts(accountId: number) {
    return this.http.get<
      {
        insurance_product_id: number;
        name: string;
        number_of_policies: number;
      }[]
    >(
      `${this.apiUrl}/accounts/dashboard/fee-schedule-unmatched-products/${accountId}`,
    );
  }

  getUnmatchedClaimsPercent(accountId: number) {
    return this.http.get<{ unmatchedClaimsPercent: number }>(
      `${this.apiUrl}/accounts/dashboard/unmatched-claims-percent/${accountId}`,
    );
  }

  payerProductUnmatchedClaims(accountId: number) {
    return this.http.get<
      {
        insurance_product_id: number;
        name: string;
        unmatched_claims: number;
        claim_count: number;
      }[]
    >(
      `${this.apiUrl}/accounts/dashboard/payer-product-unmatched-claims/${accountId}`,
    );
  }
}
